'use client';

import { useEffect, useRef } from 'react';

const stats = [
  { value: '8+', label: 'Years of Experience', icon: '📅' },
  { value: '240+', label: 'Projects Completed', icon: '🚀' },
  { value: '30+', label: 'Countries Served', icon: '🌍' },
  { value: '98%', label: 'Client Satisfaction', icon: '⭐' },
];

const trustPoints = [
  { title: 'US Registered LLC', desc: 'Incorporated under Wyoming state law — legally accountable and internationally recognized.', icon: '🏛️' },
  { title: 'Transparent Pricing', desc: 'Every project is quoted in USD with a fixed scope. No surprise invoices or scope creep.', icon: '📊' },
  { title: 'Structured Payments', desc: '50% deposit to start, 50% on approved delivery. Payments via Stripe or PayPal only.', icon: '🔒' },
  { title: 'Direct Communication', desc: 'You work directly with our team leads. No middlemen, no offshore handoffs.', icon: '💬' },
  { title: 'Full Asset Ownership', desc: 'Upon final payment, you own all deliverables outright — source files included.', icon: '📁' },
  { title: 'Post-Delivery Support', desc: '30 days of complimentary support after project delivery for every client.', icon: '🛡️' },
];

export default function TrustSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.1 }
    );
    sectionRef?.current?.querySelectorAll('.reveal-hidden, .reveal-left, .reveal-right')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="trust" ref={sectionRef} className="py-24 lg:py-32" style={{ background: '#F8FAFF' }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden">
          <span className="section-label">Why Cartelix</span>
          <div className="section-divider mx-auto" />
          <h2
            className="font-heading font-extrabold leading-tight mb-4"
            style={{ fontSize: 'clamp(2rem, 3.5vw, 2.8rem)', color: '#0D1A3A', letterSpacing: '-0.025em' }}
          >
            Built on Accountability.<br />Proven by Results.
          </h2>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-16">
          {stats?.map((stat, idx) => (
            <div
              key={stat?.label}
              className="stat-card reveal-hidden"
              style={{ transitionDelay: `${idx * 80}ms` }}
            >
              <div className="text-3xl mb-3">{stat?.icon}</div>
              <p className="stat-number">{stat?.value}</p>
              <p className="text-sm mt-1 font-semibold" style={{ color: '#6B7280' }}>{stat?.label}</p>
            </div>
          ))}
        </div>

        {/* Trust Points Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {trustPoints?.map((point, idx) => (
            <div
              key={point?.title}
              className="reveal-hidden p-6 rounded-2xl bg-white"
              style={{
                border: '1px solid #E5E9F5',
                transitionDelay: `${idx * 70}ms`,
              }}
            >
              <div className="text-2xl mb-3">{point?.icon}</div>
              <h4 className="font-heading font-bold mb-2" style={{ fontSize: '15px', color: '#0D1A3A' }}>
                {point?.title}
              </h4>
              <p style={{ fontSize: '13.5px', color: '#6B7280', lineHeight: 1.65 }}>{point?.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}